import type { BuildingGraphPlane } from "building-types/building/buildingGraphTypes";
import type { BuildingGraphFilterBuilding } from "building-types/buildingGraphFilter/buildingGraphFilterBuilding";
import type { PropertyPath } from "building-types/buildingGraphFilter/propertyCatalog";
import { getBuildingGraphPlaneTypeFromNormalToZAxisAngle } from "../../generationTools/generateBuildingGraph";
import {
  planeExteriorPlanarGraphFaceCount,
  planeFaceNodeIds,
  planeIncidentFaceEdgeIndices,
  planeNormalToZAxisAngle,
  planePlanarGraphFaceCount,
} from "../entityFilterSelectors";
import {
  type EntityFilterSource,
  type PropertyDescriptorForPath,
  type PropertyValue,
} from "../entityFilterSourceTypes";

const WALL_PLANE_DESCRIPTORS: { [P in PropertyPath<"WALL_PLANE">]: PropertyDescriptorForPath<"WALL_PLANE", P> } = {
  planeHash: { kind: "SCALAR", type: "string" },
  planeTopologyIds: { kind: "SET", type: "id" },
  normalToZAxisAngle: { kind: "SCALAR", type: "number" },
  planeType: { kind: "SCALAR", type: "enum", values: ["VERTICAL", "HORIZONTAL", "OTHER"] },
  faceNodeIds: { kind: "SET", type: "id" },
  planarGraphFaceCount: { kind: "SCALAR", type: "number" },
  exteriorPlanarGraphFaceCount: { kind: "SCALAR", type: "number" },
  hasExteriorPlanarGraphFace: { kind: "SCALAR", type: "boolean" },
  incidentFaceEdgeIndices: { kind: "SET", type: "number" },
};

const WALL_PLANE_READERS: {
  [P in PropertyPath<"WALL_PLANE">]: (row: BuildingGraphPlane, building: BuildingGraphFilterBuilding) => PropertyValue;
} = {
  planeHash: (plane) => ({ kind: "SCALAR", value: plane.planeHash }),
  planeTopologyIds: (plane) => ({ kind: "SET", values: new Set(plane.planeTopologyIds) }),
  normalToZAxisAngle: (plane, building) => ({ kind: "SCALAR", value: planeNormalToZAxisAngle(plane, building) }),
  planeType: (plane, building) => {
    const angle = planeNormalToZAxisAngle(plane, building);
    return {
      kind: "SCALAR",
      value: angle === undefined ? undefined : getBuildingGraphPlaneTypeFromNormalToZAxisAngle(angle),
    };
  },
  faceNodeIds: (plane, building) => ({ kind: "SET", values: planeFaceNodeIds(plane, building) }),
  planarGraphFaceCount: (plane, building) => ({ kind: "SCALAR", value: planePlanarGraphFaceCount(plane, building) }),
  exteriorPlanarGraphFaceCount: (plane, building) => ({
    kind: "SCALAR",
    value: planeExteriorPlanarGraphFaceCount(plane, building),
  }),
  hasExteriorPlanarGraphFace: (plane, building) => ({
    kind: "SCALAR",
    value: planeExteriorPlanarGraphFaceCount(plane, building) > 0,
  }),
  incidentFaceEdgeIndices: (plane, building) => ({
    kind: "SET",
    values: planeIncidentFaceEdgeIndices(plane, building),
  }),
};

export const wallPlaneSource: EntityFilterSource<"WALL_PLANE"> = {
  sourceId: "WALL_PLANE",
  *iterateRows(building) {
    for (const planeHash in building.buildingGraph.buildingPlanes) {
      const plane = building.buildingGraph.buildingPlanes[planeHash];
      if (plane) {
        yield plane;
      }
    }
  },
  identity(plane) {
    return plane.planeHash;
  },
  descriptor(path) {
    return WALL_PLANE_DESCRIPTORS[path];
  },
  readProperty(plane, path, building) {
    return WALL_PLANE_READERS[path](plane, building);
  },
};
